import React from "react";
import {
  PaginationWrap,
  PaginationInfo,
  PaginationLeft,
  PageSizeBlock,
  PageSizeSelect,
  PaginationControls,
  PageButton,
  Ellipsis,
} from "./Table.style";

const ChevronLeft = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

/**
 * Returns array of page items: numbers or 'ellipsis'. Always includes first and last page; uses … for gaps.
 */
function pageItems(current, totalPages, windowSize = 2) {
  if (totalPages <= 0) return [];
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => ({ type: "page", value: i + 1 }));
  const first = 1;
  const last = totalPages;
  const left = Math.max(first, current - windowSize);
  const right = Math.min(last, current + windowSize);
  const pages = new Set([first, last, ...Array.from({ length: right - left + 1 }, (_, i) => left + i)]);
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push({ type: "ellipsis" });
    result.push({ type: "page", value: sorted[i] });
  }
  return result;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50];

/** @param hidePageButtonsWhenSinglePage Hide page controls when total pages ≤ 1 (legacy FOS list). */
export default function TablePagination({
  currentPage,
  pageSize,
  total,
  onPageChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
  hidePageButtonsWhenSinglePage = false,
}) {
  const totalPages = total === 0 ? 0 : Math.max(1, Math.ceil(total / pageSize));
  const showPageButtons = !(hidePageButtonsWhenSinglePage && totalPages <= 1);
  const start = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, total);
  const showPageSizeSelect = Boolean(onPageSizeChange && pageSizeOptions?.length);

  return (
    <PaginationWrap>
      <PaginationLeft>
        {showPageSizeSelect && (
          <PageSizeBlock>
            <span>Show</span>
            <PageSizeSelect
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label="Items per page"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </PageSizeSelect>
            <span>per page</span>
          </PageSizeBlock>
        )}
        <PaginationInfo>
          Showing {start}–{end} of {total}
        </PaginationInfo>
      </PaginationLeft>
      {showPageButtons ? (
        <PaginationControls>
          <PageButton
            type="button"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft />
          </PageButton>
          {pageItems(currentPage, totalPages).map((item, i) =>
            item.type === "ellipsis" ? (
              <Ellipsis key={`ellipsis-${i}`}>…</Ellipsis>
            ) : (
              <PageButton
                key={item.value}
                type="button"
                data-active={item.value === currentPage}
                onClick={() => onPageChange(item.value)}
                aria-label={`Page ${item.value}`}
              >
                {item.value}
              </PageButton>
            )
          )}
          <PageButton
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            aria-label="Next page"
          >
            <ChevronRight />
          </PageButton>
        </PaginationControls>
      ) : null}
    </PaginationWrap>
  );
}
